
import { useState, useEffect } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { useTranslation } from "react-i18next"
import { apiRequest } from "@/lib/api-client"
import { Loader2, RefreshCw } from "lucide-react"

interface LoginQRDialogProps {
  userId: string
  userName: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function LoginQRDialog({ userId, userName, open, onOpenChange }: LoginQRDialogProps) {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(true)
  const [token, setToken] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [qrUrl, setQrUrl] = useState<string | null>(null)

  useEffect(() => {
    if (open && userId) {
      generateQRCode()
    }
  }, [open, userId])

  const generateQRCode = async () => {
    setIsLoading(true)
    setError(null)
    setQrUrl(null)

    try {
      const response = await apiRequest({
        method: "POST",
        url: `/auth/login/qr/generate/${userId}`,
      })

      if (response && response.token) {
        setToken(response.token)
        // Generate QR code using a free API service
        const encodedToken = encodeURIComponent(response.token)
        setQrUrl(`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodedToken}`)
      } else {
        setError(t("qrCode.errorInvalidResponse"))
      }
    } catch (err) {
      console.error("Failed to generate QR code:", err)
      setError(err instanceof Error ? err.message : t("qrCode.errorGeneral"))
    } finally {
      setIsLoading(false)
    }
  }

  // Fallback method to display token as text
  const renderTokenAsText = () => {
    if (!token) return null

    return (
      <div className="bg-gray-100 p-4 rounded-lg overflow-auto max-w-full">
        <p className="text-xs font-mono break-all">{token}</p>
      </div>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("qrCode.title")}</DialogTitle>
          <DialogDescription>{t("qrCode.description", { userName })}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center justify-center p-4">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center p-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="mt-2 text-sm text-muted-foreground">{t("qrCode.generating")}</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center p-4">
              <p className="text-destructive">{error}</p>
              <Button onClick={generateQRCode} className="mt-4">
                {t("qrCode.tryAgain")}
              </Button>
            </div>
          ) : token ? (
            <div className="flex flex-col items-center justify-center">
              {qrUrl ? (
                <div className="bg-white p-4 rounded-lg">
                  <img
                    src={qrUrl || "/placeholder.svg"}
                    alt="Login QR Code"
                    width={200}
                    height={200}
                    onError={() => setQrUrl(null)}
                  />
                </div>
              ) : (
                <div className="mt-4">
                  <p className="text-sm text-amber-600 mb-2">{t("qrCode.renderFailed")}</p>
                  {renderTokenAsText()}
                </div>
              )}

              <p className="mt-4 text-sm text-muted-foreground text-center">{t("qrCode.scanInstructions")}</p>

              <Button onClick={generateQRCode} className="mt-6" variant="secondary" disabled={isLoading}>
                <RefreshCw className="mr-2 h-4 w-4" />
                {t("qrCode.regenerateButton")}
              </Button>
            </div>
          ) : null}
        </div>

        <div className="flex justify-between mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.close")}
          </Button>
          {!isLoading && token && (
            <Button onClick={generateQRCode} variant="default">
              {t("qrCode.regenerate")}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
